<template>
  <div>
    <div style="text-align: right;">
      <CButton
        color="success"
        :disabled="state === 'preparing'"
        @click="openModal"
      >
        Download multiple creatives
      </CButton>
      <div
        v-if="state === 'preparing'"
        class="text-warning font-weight-bold mt-1"
      >
        Preparing files for download...
      </div>
      <div v-if="state === 'ready' || downloadState === 'downloading'">
        <a
          v-if="fileCount > 0 && state === 'ready' && downloadState !== 'downloading'"
          href="#"
          class="font-weight-bold"
          @click.prevent="startDownload"
        >
          Download {{ fileCount }} files
        </a>
        <DownloadProgress
          v-else-if="downloadState === 'downloading'"
          style="width: 250px; margin-left: auto;"
          :downloaded-count="downloadedCount"
          :download-file-count="downloadFileCount"
          :download-progress="downloadProgress"
        />
        <span
          v-else
          class="text-muted font-weight-bold"
        >
          No files to download
        </span>
        <div
          v-if="missingIds.length"
          class="text-warning mt-1"
          style="max-width: 350px; margin-left: auto;"
        >
          No results for: {{ truncatedMissingIds }}
          <a
            v-if="missingIds.length > 2 && !showAllMissingIds"
            href="#"
            class="text-warning font-weight-bold"
            @click.prevent="showAllMissingIds = true"
          >
            show {{ missingIds.length - 2 }} more
          </a>
        </div>
      </div>
    </div>

    <CModal
      title="Download multiple creatives"
      :show.sync="modalVisible"
    >
      <CTextarea
        v-model="idsInput"
        placeholder="Paste creative IDs separated by commas or newlines"
        rows="6"
      />

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="state === 'preparing'"
          @click="submitDownload"
        >
          Submit
        </ButtonLoading>

        <CButton
          color="danger"
          @click="modalVisible = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import { showErrorMessage } from '@/notification-utils'
import CreativeApi from '@/api/creative.api'
import iframeDownloadMixin from '@/mixins/iframe-download-mixin'
import DownloadProgress from '@/components/common/DownloadProgress.vue'

export default {
  name: 'BulkDownloadCreatives',

  components: { DownloadProgress },

  mixins: [iframeDownloadMixin],

  data() {
    return {
      modalVisible: false,
      idsInput: '',
      state: 'idle',
      files: [],
      fileCount: 0,
      missingIds: [],
      showAllMissingIds: false
    }
  },

  computed: {
    truncatedMissingIds() {
      if (this.showAllMissingIds || this.missingIds.length <= 2) {
        return this.missingIds.join(', ')
      }

      return this.missingIds.slice(0, 2).join(', ') + ', '
    }
  },

  methods: {
    openModal() {
      this.idsInput = ''
      this.modalVisible = true
    },

    async submitDownload() {
      const creativeIdPattern = /^CR[0-9a-f]{32}$/i
      const creativeIds = this.idsInput
        .split(/[,\n]/)
        .map(id => id.trim())
        .filter(id => id)

      if (!creativeIds.length) {
        return
      }

      const invalidIds = creativeIds.filter(id => !creativeIdPattern.test(id))
      if (invalidIds.length) {
        showErrorMessage(`Invalid creative IDs: ${invalidIds.join(', ')}`)
        return
      }

      this.modalVisible = false
      this.state = 'preparing'
      this.showAllMissingIds = false

      try {
        const response = await CreativeApi.bulkDownload(creativeIds)
        const data = response.data || response

        const files = []
        for (const creative of data) {
          if (creative.files) {
            files.push(...creative.files)
          }

          if (creative.approvalDocs) {
            files.push(...creative.approvalDocs)
          }
        }

        const returnedIds = new Set(data.map(c => c.creativeId))
        this.missingIds = creativeIds.filter(id => !returnedIds.has(id))
        this.files = files
        this.fileCount = files.length
        this.state = 'ready'
      } catch (error) {
        showErrorMessage('Failed to prepare bulk download')
        this.state = 'idle'
      }
    },

    async startDownload() {
      await this.startIframeDownload(this.files)
      this.state = 'idle'
    }
  }
}
</script>
